"use client";

import { useTranslations } from "next-intl";

import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

type CountdownTimerProps = {
  initialSeconds: number;
  onExpire: () => void;
  className?: string;
};

export function CountdownTimer({
  initialSeconds,
  onExpire,
  className = "",
}: CountdownTimerProps) {
  const t = useTranslations("checkout");
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isWarning, setIsWarning] = useState(false);

  useEffect(() => {
    if (seconds <= 0) {
      onExpire();
      return;
    }
    if (seconds <= 60 && !isWarning) {
      setIsWarning(true);
    }
    const timer = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds, onExpire, isWarning]);

  const formatTime = () => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // on utilise une base orange qui marche en light & dark
  const baseColor = "text-orange-600 dark:text-orange-400";
  const warnColor = "text-orange-800 dark:text-orange-200";

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 bg-linear-to-r from-orange-500/30 via-orange-400/20 to-transparent backdrop-blur-xs border-t border-b border-orange-200/20 dark:border-orange-800/20",
        isWarning ? warnColor : baseColor,
        className,
      )}
    >
      <Clock className="h-4 w-4" />
      <span className="text-sm font-medium">
        {t("expiresIn", { time: formatTime() })}
      </span>
    </div>
  );
}
